"use client";

import type { HrtOption } from "@/app/directories/shop/[clientSlug]/products/[productId]/components/ProductContext";
import { formatAsDollars } from "@/app/directories/shop/lib/formatAsDollars";
import { ProductType } from "@/lib/graphql/generated-gql";
import type { Hrt } from "@/lib/graphql/generated-gql";
import { getAllEnabledOrgConfigFlagsByOrgId } from "@frontend/lib/actions/actions";
import { EVENT_CONTEXTS } from "@frontend/lib/analytics";
import { trackEvent } from "@frontend/lib/analytics";
import { EVENT_CATEGORIES } from "@frontend/lib/analytics";
import type { GetProductsByCategoryIdQuery } from "@frontend/lib/graphql/generated-gql";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import { sendGTMEvent } from "@next/third-parties/google";
import { useIsFetching, useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { PriceExplainabilityRenderer } from "../PriceExplainability/PriceExplainabilityRenderer";
import { useInsuranceGatedToggle } from "@frontend/components/InsuranceConnector/insurance-connector-provider";
import { PriceToggle } from "../priceToggle/priceToggle";
import { usePriceMap } from "../priceToggle/usePriceMap";

export const ProductCardCarouselSlide = ({
  product,
  clientSlug,
  showBuyNowAndViewDetails,
  primaryColor,
  orgId,
  isMinimal = false,
  onBuyNowClick,
}: {
  product: GetProductsByCategoryIdQuery["findManyProducts"][number];
  clientSlug: string;
  showBuyNowAndViewDetails: boolean;
  primaryColor?: string | null;
  orgId: number;
  isMinimal?: boolean;
  onBuyNowClick?: () => void;
}) => {
  const pricingStatus = useBoundStore.use.pricingStatus();
  const isFetchingPrice = !!useIsFetching({ queryKey: ["updatePrices"] });
  const pricesLoaded = pricingStatus === "SUCCESS" && !isFetchingPrice;
  const t = useTranslations("Shop");
  const { data: configFlags } = useQuery({
    queryKey: ["configFlags", orgId],
    queryFn: () => getAllEnabledOrgConfigFlagsByOrgId(orgId),
    enabled: !!orgId,
  });

  const {
    isInsuredMode,
    handlePriceModeChange,
    lowestPrice,
    isToggleDisabled,
    lowestComparisonPrice,
    isSelfPayOnly,
  } = usePriceMap(product.productToHrt as unknown as HrtOption[]);

  const { createGatedHandler } = useInsuranceGatedToggle();
  const gatedPriceModeChange = createGatedHandler(handlePriceModeChange);

  const handleBuyNowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (
      (product.product_type === ProductType.PdpAndBuyNow ||
        product.product_type === ProductType.BuyNow) &&
      onBuyNowClick
    ) {
      onBuyNowClick();

      sendGTMEvent({
        event: "featured_product_clicked",
        value: product.name,
      });
      trackEvent({
        category: EVENT_CATEGORIES.TRANSACTION,
        context: EVENT_CONTEXTS.STOREFRONT,
        action: "featured_product_clicked",
        metadata: {
          product_name: product.name,
        },
      });
    }
  };

  return (
    <div
      key={`product-card-${product.id}`}
      className="keen-slider__slide py-4 overflow-clip"
      style={{
        width: "400px",
        minWidth: "400px",
        maxWidth: "400px",
        flexShrink: 0,
      }}
    >
      <div className="rounded-3xl overflow-hidden flex flex-col shadow-lg w-full bg-white h-full">
        {product.displayMetadata?.imageUrl && (
          <Link
            href={`/${clientSlug}/products/${product.id}`}
            className="w-full h-[300px]"
          >
            <img
              src={product.displayMetadata.imageUrl}
              alt={product.name}
              width={400}
              height={250}
              className="w-full h-full object-cover"
            />
          </Link>
        )}

        <div className="px-6 py-4">
          <h3 className="text-xl lg:text-4xl config-h4 font-normal">
            {product.name}
          </h3>
          {product.displayMetadata?.description?.[0]?.value && (
            <p className="config-p3 line-clamp-2 pt-3">
              {product.displayMetadata.description[0].value}
            </p>
          )}
        </div>

        {/* Price Display */}
        <div className="px-6">
          {isMinimal ? (
            <p className="text-xs font-light">
              {product.productToHrt.length > 1 && <span>Starting at </span>}
              {product.displayMetadata?.preDiscount && (
                <span className="font-mono line-through mr-1">
                  ${product.displayMetadata?.preDiscount}
                </span>
              )}
              <span className="font-mono">
                {formatAsDollars(lowestPrice, t)}
              </span>
              {!configFlags?.no_insured_procedures && " without insurance"}
            </p>
          ) : (
            <div className="w-full min-w-0 overflow-hidden">
              <PriceToggle
                checked={isInsuredMode}
                onCheckedChange={gatedPriceModeChange}
                price={lowestPrice}
                lowestComparisonPrice={lowestComparisonPrice}
                disabled={isToggleDisabled}
                hasMultipleHrts={product.productToHrt.length > 1}
                isSelfPayOnlyClient={!!configFlags?.no_insured_procedures}
                isProductSelfPayOnly={isSelfPayOnly}
                isCategoryProduct={false}
                isCardCarousel={true}
                preDiscountprice={product.displayMetadata?.preDiscount}
                pricesLoaded={pricesLoaded}
                priceToggleColor={undefined}
                isFetchingPrice={isFetchingPrice}
              />
              <div className="mt-2">
                <PriceExplainabilityRenderer
                  hrt={
                    {
                      ...product.productToHrt[0].hrt,
                      orgId: product.orgId,
                    } as Hrt
                  }
                />
              </div>
            </div>
          )}
        </div>

        <div className="p-6 flex flex-col gap-3">
          {/* Action Buttons */}
          {showBuyNowAndViewDetails ? (
            <div className="flex flex-row lg:flex-col items-center gap-2 mt-auto">
              <button
                type="button"
                className="rounded-full text-white py-3 px-6 w-full text-center"
                style={{ backgroundColor: primaryColor || "#000000" }}
                onClick={handleBuyNowClick}
              >
                {t("BookNow")}
              </button>
              <Link
                href={`/${clientSlug}/products/${product.id}`}
                className="rounded-full font-light underline border-black py-3 px-6 w-full text-center"
                style={{ color: primaryColor || "#000000" }}
              >
                {t("ViewDetails")}
              </Link>
            </div>
          ) : (
            <Link
              href={`/${clientSlug}/products/${product.id}`}
              className="rounded-full text-white py-3 px-6 w-full text-center mt-auto"
              style={{ backgroundColor: primaryColor || "#000000" }}
            >
              {t("ViewDetails")}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};
